import React from 'react';
import m from './MealsStepMobile.module.css';
import s from '../DietPlanBuilder.module.css';
import { Icon, Pill, toneFor } from '../ui';
import { copy } from '../../copy';
import { fmtG, fmtInt } from '../../lib/format';
import { totals } from '../../lib/nutrition';
import type { BuilderDerived, Dispatch } from '../../hooks/useDietPlanBuilder';
import type { Plan } from '../../types';

export function MealsStepMobile({
  plan,
  derived,
  dispatch,
}: {
  plan: Plan;
  derived: NonNullable<BuilderDerived>;
  dispatch: Dispatch;
}) {
  const dayOk = derived.offOpts === 0;
  return (
    <div className={m.page}>
      <div className={m.summary}>
        <div className={m.summaryCopy}>
          <div className={m.summaryTitle}>{fmtInt(plan.kcal)} kcal · {derived.active.length} meals</div>
          <div className={m.summarySub}>{dayOk ? copy.meals.allOk : copy.meals.off(derived.offOpts)}</div>
        </div>
        <Pill tone={dayOk ? 'green' : derived.offOpts > 1 ? 'red' : 'amber'}>
          {dayOk ? copy.meals.onTargetStatus : copy.meals.optionOff(derived.offOpts)}
        </Pill>
      </div>

      <div className={m.timeline}>
        {derived.stats.map((st, mi) => {
          const skip = st.m.skip;
          return (
            <div key={st.m.key} className={m.slot}>
              <div className={m.rail}>
                <div className={m.time}>{st.m.time}</div>
                <div className={m.dot} style={{ background: st.m.color, boxShadow: `0 0 0 1.5px ${st.m.color}` }} />
                <div className={m.line} />
              </div>
              <div className={m.body}>
                <div className={m.slotHead}>
                  <div className={m.slotCopy}>
                    <div className={m.slotName} data-skip={skip || undefined}>{st.m.name}</div>
                    <div className={m.slotTarget}>
                      {skip ? copy.meals.skipped : `${fmtInt(st.T.k)} kcal · ${fmtG(st.T.p)} / ${fmtG(st.T.c)} / ${fmtG(st.T.f)} g`}
                    </div>
                  </div>
                  {skip ? (
                    <button
                      type="button"
                      className={s.btnOutlineGreen}
                      style={{ height: 36, padding: '0 12px', fontSize: 12 }}
                      onClick={() => dispatch({ type: 'plan', fn: p => { p.meals[mi].skip = false; } })}
                    >
                      {copy.meals.include}
                    </button>
                  ) : (
                    <span className={m.count}>{st.m.options.length} option{st.m.options.length === 1 ? '' : 's'}</span>
                  )}
                </div>

                {skip ? (
                  <div className={m.skipped}>{copy.meals.skipped}</div>
                ) : (
                  <div className={s.optRow}>
                    {st.m.options.map((o, oi) => {
                      const info = st.opts[oi];
                      const tot = totals(o.foods);
                      return (
                        <button
                          type="button"
                          key={oi}
                          className={s.optCard}
                          onClick={() => dispatch({ type: 'sheet', sheet: 'opt', mi, oi })}
                        >
                          <div className={s.optCardHead}>
                            <div className={s.optCardLabel}>{copy.meals.option} {oi + 1}</div>
                            <Pill tone={toneFor(info.st)}>{info.label}</Pill>
                          </div>
                          <div className={s.optLines}>
                            {o.foods.slice(0, 3).map((f, fi) => (
                              <div key={fi} className={s.optLine}>
                                <span>{f.name}</span>
                                <span>×{fmtG(f.qty)}{f.unit && f.unit !== 'g' ? ` ${f.unit}` : ''}</span>
                              </div>
                            ))}
                            {!o.foods.length && <div className={m.empty}>{copy.meals.noFoods}</div>}
                          </div>
                          <div className={s.optCardFoot}>
                            <span className={s.optCardKcal}>{fmtInt(tot.k)}</span>
                            <span className={s.optCardKcalUnit}>kcal</span>
                            <span className={s.optCardMacros}>
                              P {fmtG(tot.p)} · C {fmtG(tot.c)} · F {fmtG(tot.f)}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={s.optAdd}
                      onClick={() => dispatch({ type: 'addOption', mi })}
                    >
                      <Icon.Plus size={20} />
                      {copy.meals.option}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
